import {
  type ConditionalDisplayConfig,
  clientRecordConditionValue,
  compareFieldCondition,
  FOLLOWUP_MODE_SOURCE,
  followupModeConditionValue,
  GOAL_RISK_SCORE_CAPPED_SOURCE,
  GOAL_TYPE_SOURCE,
  isProductAssessmentSource,
  isQuizResultSource,
  isQuizScoreSource,
  matchScoreCondition,
  PORTFOLIO_SCHEDULE_SOURCE,
  PORTFOLIO_TYPE_SOURCE,
  quizIdFromResultSource,
  quizIdFromScoreSource,
  readProductAssessmentSource,
  readQuizScoreRange,
} from "@repo/shared";
import {
  type ConfiguredAssessmentType,
  parseTrapetsSnapshot,
  readQuizResultStatus,
  trapetsPepAnswer,
  trapetsSanctionedAnswer,
} from "@repo/shared/types";
import { FlowComponentHeader } from "@/components/ui/flow-component-header";
import type { ColumnConfig } from "@/lib/types/flow";
import type { ClientConditionData } from "./flow-wizard-types";

// ─── Condition Evaluation Data ───────────────────────────────────────────────

/**
 * Data shape accepted by evaluateCondition().
 * All virtual fields are derived on-the-fly from these sources —
 * no pre-synced formValues store needed.
 */
export type ConditionEvaluationData = {
  fieldValues: Array<{
    flowFieldId: string;
    value: string;
    goalId: string | null;
    portfolioId?: string | null;
  }>;
  classificationValue?: string | null;
  trapetsLookupValue?: string | null;
  sumsubVerificationValue?: string | null;
  /** QL-13 — the `quiz` component's calculatedData blob; missing reads as "unanswered". */
  quizValue?: string | null;
  /**
   * QL-361 — the `product_assessment` component's calculatedData blob and the
   * collection's configured assessment types; together they resolve `__pa_…`.
   */
  productAssessmentValue?: string | null;
  configuredAssessmentTypes?: ConfiguredAssessmentType[];
  goals: Array<{
    id: string;
    /** QP-150 — portfolioType/scheduleType live on goal_portfolio, ordered by portfolio order. */
    portfolios: Array<{
      id: string;
      portfolioType: string;
      scheduleType: string;
      /** QL-774 — server-derived follow-up lineage; feeds `__followup_mode__`. */
      isFollowup?: boolean;
    }>;
    riskScore: number | null;
    hasRiskAssessment: boolean;
    /** QL-120 — the goal's stored type (goal-type row id or GOAL_TYPE_NONE). */
    goalType: string;
    /** QL-71 — server-derived `__goal_risk_score_capped__` state (one of RISK_SCORE_CAP_STATES). */
    riskScoreCapState: string;
  }>;
  clientData?: ClientConditionData;
  /**
   * QPBI-395 — source flow field id → field type ("boolean" | "multiselect" | …).
   * Lets the rule comparison see only what the control renders. Sentinel
   * sources (`__*__`, portfolio type/schedule) are absent by design and keep
   * the lenient string semantics.
   */
  fieldTypes?: Record<string, string>;
  goalId?: string;
  portfolioId?: string;
};

/**
 * Resolve a portfolio-scoped conditional source (QPBI-321). Requires the
 * current portfolio — portfolio attributes are never evaluated at goal/global
 * scope and there is NO fallback. Mirrored by the backend completion engine
 * (flow-validation.ts) and public-flow-utils.ts, keep the three in sync.
 */
export function getPortfolioConditionField(
  goals: ConditionEvaluationData["goals"],
  goalId: string | undefined,
  portfolioId: string | undefined,
  field: "portfolioType" | "scheduleType",
): string {
  if (!goalId || !portfolioId) return "";
  const portfolios = goals.find((g) => g.id === goalId)?.portfolios ?? [];
  return portfolios.find((p) => p.id === portfolioId)?.[field] || "";
}

// ─── Snapshot Virtual Field Helpers ──────────────────────────────────────────

function deriveClassificationType(classificationValue: string | null | undefined): string {
  if (!classificationValue) return "";
  try {
    return JSON.parse(classificationValue).classificationType || "";
  } catch {
    return "";
  }
}

/**
 * QL-g — must stay identical to the backend's `deriveTrapetsField`, which is
 * why both now call the same shared rule instead of each reading
 * `status[field]`. A frontend that disagrees with the engine shows the advisor
 * a page the server considers hidden.
 */
function deriveTrapetsField(
  trapetsLookupValue: string | null | undefined,
  field: "isPEP" | "isSanctioned",
): string {
  const snapshot = parseTrapetsSnapshot(trapetsLookupValue);
  return field === "isPEP" ? trapetsPepAnswer(snapshot) : trapetsSanctionedAnswer(snapshot);
}

function deriveSumsubVerified(sumsubVerificationValue: string | null | undefined): string {
  if (!sumsubVerificationValue) return "unchecked";
  try {
    const d = JSON.parse(sumsubVerificationValue);
    if (!d.hasBeenChecked) return "unchecked";
    return d.status?.isVerified ? "yes" : "no";
  } catch {
    return "unchecked";
  }
}

/**
 * QL-68 — the alpha-3 issuing country of the document Sumsub verified
 * (`info.country`), or "" until a check has completed. Compared raw against
 * the alpha-3 the rule stores, so no translation table sits on the path.
 */
function deriveSumsubDocumentCountry(v: string | null | undefined): string {
  if (!v) return "";
  try {
    const d = JSON.parse(v);
    if (!d.hasBeenChecked) return "";
    return typeof d.info?.country === "string" ? d.info.country : "";
  } catch {
    return "";
  }
}

// ─── evaluateCondition ───────────────────────────────────────────────────────

/**
 * Evaluate a conditional display rule.
 * Derives all virtual fields on-the-fly from the provided data sources.
 *
 * @param condition The conditional display configuration
 * @param data All data sources needed for evaluation
 * @returns true if the component should be shown, false if hidden
 */
export function evaluateCondition(
  condition: ConditionalDisplayConfig | null | undefined,
  data: ConditionEvaluationData,
): boolean {
  if (!condition || !condition.enabled) {
    return true;
  }

  let fieldValue = "";
  const sourceFieldId = condition.sourceFieldId;

  // ── Client-based conditions ──
  // QL-323 / QL-217 — type and country through the reader the public flow
  // and the completion engine share.
  const clientRecordValue = clientRecordConditionValue(sourceFieldId, data.clientData);
  if (clientRecordValue !== undefined) {
    fieldValue = clientRecordValue;
  } else if (sourceFieldId.startsWith("__custom_field_")) {
    const customFieldId = sourceFieldId.replace("__custom_field_", "");
    const customValue = data.clientData?.customValues?.find(
      (cv) => cv.customField?.id === customFieldId,
    );
    fieldValue = customValue?.value || "";
  }
  // ── Snapshot-derived virtual fields ──
  else if (sourceFieldId === "__classification_type__") {
    fieldValue = deriveClassificationType(data.classificationValue);
  } else if (sourceFieldId === "__trapets_pep__") {
    fieldValue = deriveTrapetsField(data.trapetsLookupValue, "isPEP");
  } else if (sourceFieldId === "__trapets_sanctioned__") {
    fieldValue = deriveTrapetsField(data.trapetsLookupValue, "isSanctioned");
  } else if (sourceFieldId === "__sumsub_verified__") {
    fieldValue = deriveSumsubVerified(data.sumsubVerificationValue);
  } else if (sourceFieldId === "__sumsub_document_country__") {
    fieldValue = deriveSumsubDocumentCountry(data.sumsubVerificationValue);
  } else if (isQuizResultSource(sourceFieldId)) {
    // QL-13 — "unanswered" | "passed" | "failed"; same reader the backend
    // engine uses, so the two never disagree about what is visible.
    fieldValue = readQuizResultStatus(data.quizValue, quizIdFromResultSource(sourceFieldId));
  } else if (isQuizScoreSource(sourceFieldId)) {
    // QL-361 — the last completed attempt's score; no score never matches,
    // whatever the operator. Same shared verdict as the backend engine.
    return matchScoreCondition(
      readQuizScoreRange(data.quizValue, quizIdFromScoreSource(sourceFieldId)),
      condition.operator,
      condition.value || "",
    );
  } else if (isProductAssessmentSource(sourceFieldId)) {
    // QL-361 — same reader as the backend engine. A type or mode that is not
    // configured never matches, whatever the operator.
    const read = readProductAssessmentSource(
      sourceFieldId,
      data.productAssessmentValue,
      data.configuredAssessmentTypes ?? [],
    );
    if (!read.resolved) return false;
    if (read.measure === "score") {
      return matchScoreCondition(read.scoreRange, condition.operator, condition.value || "");
    }
    fieldValue = read.value;
  }
  // ── Session-level (QL-774): any follow-up portfolio makes it a follow-up ──
  else if (sourceFieldId === FOLLOWUP_MODE_SOURCE) {
    fieldValue = followupModeConditionValue(data.goals);
  }
  // ── Portfolio-scoped virtual fields (QPBI-321: portfolio pages only, no fallback) ──
  else if (sourceFieldId === PORTFOLIO_TYPE_SOURCE) {
    fieldValue = getPortfolioConditionField(
      data.goals,
      data.goalId,
      data.portfolioId,
      "portfolioType",
    );
  } else if (sourceFieldId === PORTFOLIO_SCHEDULE_SOURCE) {
    fieldValue = getPortfolioConditionField(
      data.goals,
      data.goalId,
      data.portfolioId,
      "scheduleType",
    );
  }
  // ── Goal-scoped virtual fields ──
  else if (sourceFieldId === "__goal_has_risk__" && data.goalId) {
    fieldValue = data.goals.find((g) => g.id === data.goalId)?.hasRiskAssessment ? "true" : "false";
  } else if (sourceFieldId === "__goal_risk_score__" && data.goalId) {
    const score = data.goals.find((g) => g.id === data.goalId)?.riskScore;
    fieldValue = score != null ? String(score) : "";
  } else if (sourceFieldId === GOAL_TYPE_SOURCE && data.goalId) {
    // QL-120 — the goal's stored type; `in`/`not_in` rules test set membership.
    // No goal in context (global page) → "" → `in` hides, `not_in` shows.
    fieldValue = data.goals.find((g) => g.id === data.goalId)?.goalType ?? "";
  } else if (sourceFieldId === GOAL_RISK_SCORE_CAPPED_SOURCE && data.goalId) {
    // QL-71 — server-derived cap state; mirrored by the engine and public-flow-utils.ts.
    // No goal in context (global page) → "" → `equals` hides, `not_equals` shows.
    fieldValue = data.goals.find((g) => g.id === data.goalId)?.riskScoreCapState ?? "";
  }
  // ── Regular field values ──
  else if (data.fieldValues) {
    // Check portfolio-scoped field first (most specific)
    if (data.portfolioId) {
      const portfolioScoped = data.fieldValues.find(
        (v) => v.flowFieldId === sourceFieldId && v.portfolioId === data.portfolioId,
      );
      if (portfolioScoped) {
        fieldValue = portfolioScoped.value;
      }
    }
    // Check goal-scoped field
    if (!fieldValue && data.goalId) {
      const goalScoped = data.fieldValues.find(
        (v) => v.flowFieldId === sourceFieldId && v.goalId === data.goalId && !v.portfolioId,
      );
      if (goalScoped) {
        fieldValue = goalScoped.value;
      }
    }
    // Fall back to global field
    if (!fieldValue) {
      const globalField = data.fieldValues.find(
        (v) => v.flowFieldId === sourceFieldId && !v.goalId && !v.portfolioId,
      );
      if (globalField) {
        fieldValue = globalField.value;
      }
    }
  }

  return compareFieldCondition(
    fieldValue,
    condition.operator,
    condition.value || "",
    data.fieldTypes?.[sourceFieldId],
  );
}

/**
 * Get CSS classes for column styling based on ColumnConfig
 */
export function getColumnClasses(columnConfig: ColumnConfig | undefined): string {
  if (!columnConfig?.style || columnConfig.style === "none") {
    return "space-y-6";
  }

  switch (columnConfig.style) {
    case "border":
      return "space-y-4 border rounded-lg p-6";
    case "box":
      return "space-y-4 rounded-lg p-6 bg-muted/50";
    default:
      return "space-y-6";
  }
}

/**
 * Render column header (title and description) if configured
 */
export function renderColumnHeader(columnConfig: ColumnConfig | undefined): React.ReactNode {
  if (!columnConfig?.title && !columnConfig?.description) {
    return null;
  }

  return <FlowComponentHeader title={columnConfig.title} description={columnConfig.description} />;
}
